import { IconCircleCheck } from "central-icons/IconCircleCheck";
import { IconCircleInfo } from "central-icons/IconCircleInfo";
import { IconCircleX } from "central-icons/IconCircleX";
import { IconCrossSmall } from "central-icons/IconCrossSmall";
import { IconTriangleExclamation } from "central-icons/IconTriangleExclamation";
import type React from "react";
import { toast as sonnerToast } from "sonner";

import type { ToastActionElement, ToastType } from "@/components/flexkit/toast/Toast";

type IconComponent = typeof IconCircleInfo;

const iconMap: Record<Exclude<ToastType, "default">, IconComponent> = {
    info: IconCircleInfo,
    success: IconCircleCheck,
    warning: IconTriangleExclamation,
    error: IconCircleX,
};

const iconColorMap: Record<ToastType, string> = {
    default: "",
    info: "text-content-muted",
    success: "text-content-success",
    warning: "text-content-warning",
    error: "text-content-destructive",
};

const borderColorMap: Record<ToastType, string> = {
    default: "border-border-base",
    info: "border-border-base",
    success: "border-border-success dark:border-border-success/30",
    warning: "border-border-warning dark:border-border-warning/30",
    error: "border-border-error dark:border-border-error/30",
};

export function ToastContent({
    id,
    title,
    description,
    action,
    type = "default",
    dismissible = true,
}: {
    id: string | number;
    title: React.ReactNode;
    description?: React.ReactNode;
    action?: ToastActionElement;
    type: ToastType;
    dismissible?: boolean;
}) {
    const hasIcon = type !== "default";
    const Icon = hasIcon ? iconMap[type] : null;
    const isCompact = !description && !action;

    return (
        <div
            className={`flex w-full gap-2 rounded-xl border bg-surface-primary p-4 shadow-lg ${isCompact ? "items-center" : "items-start"} ${borderColorMap[type]}`}
        >
            {Icon && (
                <div className={`shrink-0 ${isCompact ? "" : "pt-0.5"}`}>
                    <Icon size={20} className={iconColorMap[type]} />
                </div>
            )}
            <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-content-primary">{title}</div>
                {description && <div className="mt-1 text-sm text-content-secondary">{description}</div>}
                {action && <div className="mt-3 flex justify-end">{action}</div>}
            </div>
            {dismissible && (
                <button
                    onClick={() => sonnerToast.dismiss(id)}
                    className="shrink-0 text-content-tertiary hover:text-content-primary"
                    aria-label="Dismiss"
                    data-tracking-id="dismiss"
                >
                    <IconCrossSmall size={20} />
                </button>
            )}
        </div>
    );
}
